/**
 * Experiment Assignment API client for B2M Tracker
 *
 * Sends user assignment requests to the B2M experiments service.
 * Only responsible for the HTTP call — caching, retry and event
 * tracking live in the ExperimentManager.
 */
export default class ExperimentAPI {
  constructor(tracker) {
    this.tracker = tracker;
  }

  /**
   * Request group assignments for the given experiment IDs.
   *
   * @param {string} userId - User identifier (external or fingerprint)
   * @param {string[]} experimentIds - Active experiment UUIDs to assign
   * @param {Object} [properties={}] - Targeting properties (DeviceType, Country, …)
   * @returns {Promise<Array<{ExperimentId: string, GroupId: string, GroupName: string}>>}
   */
  async assign(userId, experimentIds, properties = {}) {
    if (!userId) {
      throw new Error('ExperimentAPI.assign: userId is required');
    }
    if (!Array.isArray(experimentIds) || experimentIds.length === 0) {
      throw new Error('ExperimentAPI.assign: experimentIds must be a non-empty array');
    }

    const url = this.tracker.config.experimentApiUrl;
    if (!url) {
      throw new Error('ExperimentAPI.assign: experimentApiUrl not configured');
    }

    // Payload is camelCase to match the experiments service contract.
    const payload = {
      userId,
      properties: properties || {},
      experimentIds
    };

    // NOTE: no x-api-key here — the experiments service does not list it in
    // its CORS Access-Control-Allow-Headers, so sending it fails the browser
    // preflight. Auth (if any) is expected via Origin / cookies.
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`ExperimentAPI.assign failed: HTTP ${response.status}`);
    }

    const data = await response.json();
    if (!Array.isArray(data)) return [];

    // Normalize to PascalCase regardless of the API's casing, so the rest of
    // the SDK (ExperimentManager, getAssignment, …) has one stable shape.
    return data.map(item => ({
      ExperimentId: item.ExperimentId ?? item.experimentId,
      GroupId: item.GroupId ?? item.groupId,
      GroupName: item.GroupName ?? item.groupName
    }));
  }
}
